import { useEffect } from 'react'
import { Link } from 'react-router-dom'
import { motion } from 'motion/react'
import { Button } from '@/components/ui'
import { useScrollReveal, useStaggeredGrid, useHeroReveal } from '@gjl/motion/css'
import { Check, ArrowRight, Mail, Send, Users, BarChart3, Clock, Sparkles } from 'lucide-react'
import { APP_URL } from '@/lib/public-trpc'
import { capability, MANAGED_SERVICES } from '@gjl/voice/pricing'

const emailCapability = capability('email')!
const managedEmail = MANAGED_SERVICES.find((s) => s.id === 'managed-email')!

const features = [
  {
    icon: Mail,
    title: 'Rich Text Editor',
    description:
      'Write beautiful emails with formatting, images, and links. No HTML knowledge required—just type like you would in a document.',
  },
  {
    icon: Clock,
    title: 'Schedule Sends',
    description: 'Write now, send later. Schedule emails for the perfect time and they will go out automatically.',
  },
  {
    icon: Users,
    title: 'Subscriber Management',
    description:
      'Build your list automatically from RSVPs, donations, and signups. Import existing contacts. Tag and segment your audience.',
  },
  {
    icon: BarChart3,
    title: 'Email Analytics',
    description:
      'Track opens, clicks, and engagement. See which emails perform best and learn what resonates with your audience.',
  },
  {
    icon: Sparkles,
    title: 'Automated Sequences',
    description:
      'Welcome new subscribers automatically. Set up drip campaigns that nurture your audience without manual work.',
  },
  {
    icon: Send,
    title: 'Reliable Delivery',
    description:
      'Built on Amazon SES for enterprise-grade deliverability. Your emails reach inboxes, not spam folders.',
  },
]

const emailTypes = [
  {
    title: 'Concert Announcements',
    description:
      'Promote upcoming performances to your full list. Include event details, ticket links, and artist info.',
  },
  {
    title: 'Season Updates',
    description:
      'Keep supporters informed about your organization. Share news, behind-the-scenes stories, and upcoming plans.',
  },
  {
    title: 'Welcome Sequences',
    description:
      'Automatically welcome new subscribers with a series of emails introducing your organization and upcoming events.',
  },
  {
    title: 'Post-Event Follow-ups',
    description:
      'Thank attendees after events. Share photos, request feedback, and invite them to upcoming performances.',
  },
]

const otherFeatures = [
  { title: 'Website', href: '/platform/features/website', description: 'Build your site' },
  { title: 'Events', href: '/platform/features/events', description: 'Manage concerts' },
  { title: 'Donations', href: '/platform/features/donations', description: 'Accept support' },
  { title: 'Analytics', href: '/platform/features/analytics', description: 'Track growth' },
]

export default function EmailFeaturePage() {
  useEffect(() => {
    document.title = 'Email Newsletters for Music Organizations | Fugue Lab Platform'
  }, [])

  // Animation refs
  const heroRef = useHeroReveal<HTMLDivElement>(0.2)
  const featuresRef = useStaggeredGrid<HTMLDivElement>('.feature-card', { stagger: 0.1 })
  const emailTypesRef = useStaggeredGrid<HTMLDivElement>('.email-type-card', { stagger: 0.1 })
  const pricingRef = useStaggeredGrid<HTMLDivElement>('.pricing-card', { stagger: 0.15 })
  const otherFeaturesRef = useStaggeredGrid<HTMLDivElement>('.other-feature-card', { stagger: 0.1 })
  const ctaRef = useScrollReveal<HTMLDivElement>('.cta-content')

  return (
    <div className="min-h-screen">
      {/* Hero */}
      <section className="px-4 py-20">
        <div ref={heroRef} className="container mx-auto max-w-4xl text-center">
          <div className="bg-primary/10 text-primary mb-6 inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium">
            <Mail className="h-4 w-4" />
            Email Newsletters
          </div>
          <h1 className="mb-6 text-4xl font-bold md:text-5xl">
            Stay connected
            <br />
            with your audience
          </h1>
          <p className="text-muted-foreground mx-auto mb-8 max-w-2xl text-xl">
            Send beautiful newsletters to your subscribers. Announce concerts, share updates, and build lasting
            relationships with your audience—without paying for another tool.
          </p>
          <div className="flex flex-col justify-center gap-4 sm:flex-row">
            <a href={`${APP_URL}/auth`}>
              <motion.div
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                transition={{ type: 'spring', stiffness: 400, damping: 25 }}
              >
                <Button size="lg" className="cursor-pointer px-8 text-lg">
                  Send Your First Email <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </motion.div>
            </a>
            <Link to="/platform">
              <motion.div
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                transition={{ type: 'spring', stiffness: 400, damping: 25 }}
              >
                <Button variant="outline" size="lg" className="cursor-pointer px-8 text-lg">
                  See All Features
                </Button>
              </motion.div>
            </Link>
          </div>
          <p className="text-muted-foreground mt-4 text-sm">
            ${emailCapability.priceMonthly}/month for up to 2,000 contacts. No send metering.
          </p>
        </div>
      </section>

      {/* Features Grid */}
      <section className="bg-muted/30 px-4 py-20">
        <div className="container mx-auto max-w-6xl">
          <div className="mb-12 text-center">
            <h2 className="mb-4 text-3xl font-bold">Email Marketing Made Simple</h2>
            <p className="text-muted-foreground mx-auto max-w-2xl">
              Everything you need to communicate with your audience, built right into the platform.
            </p>
          </div>
          <div ref={featuresRef} className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
            {features.map((feature) => (
              <motion.div
                key={feature.title}
                className="feature-card border-border bg-card rounded-lg border p-6"
                whileHover={{ y: -4 }}
                transition={{ type: 'spring', stiffness: 300, damping: 20 }}
              >
                <feature.icon className="text-primary mb-4 h-10 w-10" />
                <h3 className="mb-2 text-lg font-semibold">{feature.title}</h3>
                <p className="text-muted-foreground">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Email Types */}
      <section className="px-4 py-20">
        <div className="container mx-auto max-w-4xl">
          <div className="mb-12 text-center">
            <h2 className="mb-4 text-3xl font-bold">Emails for Every Occasion</h2>
            <p className="text-muted-foreground">
              From concert announcements to welcome sequences—communicate effectively.
            </p>
          </div>
          <div ref={emailTypesRef} className="grid gap-8 md:grid-cols-2">
            {emailTypes.map((type) => (
              <motion.div
                key={type.title}
                className="email-type-card border-border bg-card rounded-lg border p-6"
                whileHover={{ y: -4 }}
                transition={{ type: 'spring', stiffness: 300, damping: 20 }}
              >
                <h3 className="mb-2 text-lg font-semibold">{type.title}</h3>
                <p className="text-muted-foreground">{type.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* No Extra Tools */}
      <section className="bg-muted/30 px-4 py-20">
        <div className="container mx-auto max-w-4xl">
          <div className="mb-12 text-center">
            <h2 className="mb-4 text-3xl font-bold">One Platform, Everything Included</h2>
            <p className="text-muted-foreground mx-auto max-w-2xl">
              Stop paying for separate email tools. Fugue Lab includes email marketing alongside your website, events,
              and donations.
            </p>
          </div>
          <div className="grid gap-8 text-center md:grid-cols-3">
            <div>
              <div className="text-primary mb-2 text-4xl font-bold">${emailCapability.priceMonthly}</div>
              <p className="text-muted-foreground">Flat monthly price for email</p>
            </div>
            <div>
              <div className="text-primary mb-2 text-4xl font-bold">0</div>
              <p className="text-muted-foreground">Additional tools to manage</p>
            </div>
            <div>
              <div className="text-primary mb-2 text-4xl font-bold">1</div>
              <p className="text-muted-foreground">Place for all your data</p>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="px-4 py-20">
        <div className="container mx-auto max-w-4xl">
          <div className="mb-12 text-center">
            <h2 className="mb-4 text-3xl font-bold">Simple Pricing</h2>
          </div>
          <div ref={pricingRef} className="mx-auto grid max-w-3xl gap-8 md:grid-cols-2">
            <motion.div
              className="pricing-card border-primary/50 bg-primary/5 rounded-2xl border p-8"
              whileHover={{ y: -4 }}
              transition={{ type: 'spring', stiffness: 300, damping: 20 }}
            >
              <div className="text-primary mb-2 text-sm font-medium">Self-serve, available today</div>
              <h3 className="mb-2 text-2xl font-bold">{emailCapability.name}</h3>
              <div className="mb-4 flex items-baseline gap-1">
                <span className="text-4xl font-bold">${emailCapability.priceMonthly}</span>
                <span className="text-muted-foreground">/month</span>
              </div>
              <ul className="mb-8 space-y-3">
                <li className="flex items-start gap-3">
                  <Check className="mt-0.5 h-5 w-5 flex-shrink-0 text-green-500" />
                  <span className="text-muted-foreground">Newsletters, sequences, automations</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="mt-0.5 h-5 w-5 flex-shrink-0 text-green-500" />
                  <span className="text-muted-foreground">Up to 2,000 contacts</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="mt-0.5 h-5 w-5 flex-shrink-0 text-green-500" />
                  <span className="text-muted-foreground">$35/month up to 10,000 contacts</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="mt-0.5 h-5 w-5 flex-shrink-0 text-green-500" />
                  <span className="text-muted-foreground">No send metering</span>
                </li>
              </ul>
              <Link to="/get-started" className="block">
                <motion.div
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  transition={{ type: 'spring', stiffness: 400, damping: 25 }}
                >
                  <Button className="w-full cursor-pointer">Get Started</Button>
                </motion.div>
              </Link>
            </motion.div>
            <motion.div
              className="pricing-card border-border bg-card rounded-2xl border p-8"
              whileHover={{ y: -4 }}
              transition={{ type: 'spring', stiffness: 300, damping: 20 }}
            >
              <div className="text-primary mb-2 text-sm font-medium">Done for you</div>
              <h3 className="mb-2 text-2xl font-bold">{managedEmail.name}</h3>
              <div className="mb-4 flex items-baseline gap-1">
                <span className="text-4xl font-bold">${managedEmail.priceMonthly}</span>
                <span className="text-muted-foreground">/month</span>
              </div>
              <ul className="mb-8 space-y-3">
                <li className="flex items-start gap-3">
                  <Check className="mt-0.5 h-5 w-5 flex-shrink-0 text-green-500" />
                  <span className="text-muted-foreground">We plan, write, design, and send</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="mt-0.5 h-5 w-5 flex-shrink-0 text-green-500" />
                  <span className="text-muted-foreground">About 2 campaigns per month</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="mt-0.5 h-5 w-5 flex-shrink-0 text-green-500" />
                  <span className="text-muted-foreground">1 automation included</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="mt-0.5 h-5 w-5 flex-shrink-0 text-green-500" />
                  <span className="text-muted-foreground">Email Marketing capability included</span>
                </li>
              </ul>
              <Link to="/services/done-for-you#managed-email" className="block">
                <motion.div
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  transition={{ type: 'spring', stiffness: 400, damping: 25 }}
                >
                  <Button variant="outline" className="w-full cursor-pointer">
                    See Managed Email
                  </Button>
                </motion.div>
              </Link>
            </motion.div>
          </div>
          <div className="mt-8 text-center">
            <Link to="/packages" className="text-primary font-medium hover:underline">
              See All Pricing
            </Link>
          </div>
        </div>
      </section>

      {/* Other Features */}
      <section className="bg-muted/30 px-4 py-16">
        <div className="container mx-auto max-w-4xl">
          <div className="mb-8 text-center">
            <h2 className="text-2xl font-bold">More Features</h2>
            <p className="text-muted-foreground">Email is just the beginning.</p>
          </div>
          <div ref={otherFeaturesRef} className="grid grid-cols-2 gap-4 md:grid-cols-4">
            {otherFeatures.map((feature) => (
              <motion.div
                key={feature.href}
                className="other-feature-card"
                whileHover={{ y: -4 }}
                transition={{ type: 'spring', stiffness: 300, damping: 20 }}
              >
                <Link
                  to={feature.href}
                  className="border-border bg-card hover:border-primary block h-full rounded-lg border p-4 text-center transition-colors"
                >
                  <h3 className="font-semibold">{feature.title}</h3>
                  <p className="text-muted-foreground text-sm">{feature.description}</p>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-primary/5 px-4 py-16">
        <div ref={ctaRef} className="container mx-auto max-w-3xl text-center">
          <div className="cta-content">
            <h2 className="mb-4 text-3xl font-bold">Ready to connect with your audience?</h2>
            <p className="text-muted-foreground mb-8 text-xl">
              Join music organizations using Fugue Lab to stay in touch with their supporters.
            </p>
            <a href={`${APP_URL}/auth`}>
              <motion.div
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                transition={{ type: 'spring', stiffness: 400, damping: 25 }}
                className="inline-block"
              >
                <Button size="lg" className="cursor-pointer px-8 text-lg">
                  Send Your First Email <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </motion.div>
            </a>
          </div>
        </div>
      </section>
    </div>
  )
}
